import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";
import { deviceTimeZone } from "@/lib/reminderSchedule";
import { reminderOwnerSession, type ReminderOwnerGuard } from "@/lib/reminderExecutor";
import { canonicalReminderPreferencesSnapshot, reminderTimeZone } from "../../supabase/functions/_shared/reminderActionDomain";

type BootstrapResponse = { data: unknown; error: unknown };
type BootstrapQuery = PromiseLike<BootstrapResponse> & {
  setHeader(name: string, value: string): BootstrapQuery;
  abortSignal(signal: AbortSignal): BootstrapQuery;
};
type BootstrapClient = { rpc(name: string, args: { p_timezone: string }): BootstrapQuery };
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);

/** Conditional device bootstrap. The RPC fills only a missing/null timezone;
 * an explicit saved choice always wins, including a concurrent settings save.
 */
export async function ensureWhisperTimezone(userId: string, assertCurrent?: ReminderOwnerGuard): Promise<void> {
  let invalidated = false;
  const current = () => { assertCurrent?.(); if (invalidated) throw new Error("Settings account changed"); };
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user.id !== userId) invalidated = true;
  });
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    current(); const timezone = reminderTimeZone(deviceTimeZone());
    const session = await reminderOwnerSession(userId, current); current();
    const controller = new AbortController(); timer = setTimeout(() => controller.abort(), 12000);
    const { data, error } = await (supabase as unknown as BootstrapClient)
      .rpc("initialize_reminder_timezone", { p_timezone: timezone })
      .setHeader("Authorization", "Bearer " + session.access_token).abortSignal(controller.signal);
    current();
    await reminderOwnerSession(userId, current); current();
    if (error) throw error;
    if (!object(data) || data.ok !== true || data.user_id !== userId || typeof data.unchanged !== "boolean") throw new Error("Timezone bootstrap was not confirmed");
    const preferences = canonicalReminderPreferencesSnapshot(data.preferences, userId);
    if (!preferences || preferences.timezone === null || (!data.unchanged && preferences.timezone !== timezone)) throw new Error("Timezone bootstrap returned no confirmed timezone");
    // Existing configuration may differ from this device. Never replace it.
  } catch (error) {
    console.warn("[whisperPrefs] conditional timezone bootstrap failed:", error);
    if (!invalidated) try { Sentry.captureException(error, { tags: { feature: "whisper_timezone_bootstrap_failed" } }); } catch { /* optional telemetry */ }
  } finally { if (timer !== undefined) clearTimeout(timer); subscription.unsubscribe(); }
}
